import { createAction, props, union } from '@ngrx/store';
import {
  zefActionFailPayload,
  zefActionPayload,
  zefActionSuccessPayload
} from '@zerops/zef/core';
import {
  PromoCodeRedeemDto,
  PromoCodeRedemption,
  PromoCodeRedemptionListResponse
} from '@zerops/models/promo-code';
import { PendingPromoCode } from './promo-code-base.util';

export const loadPromoCodeRedemptions = createAction(
  '[@zerops/zerops/core/promo-code-base] load-redemptions',
  zefActionPayload()
);

export const loadPromoCodeRedemptionsFail = createAction(
  '[@zerops/zerops/core/promo-code-base] load-redemptions/fail',
  zefActionFailPayload
);

export const loadPromoCodeRedemptionsSuccess = createAction(
  '[@zerops/zerops/core/promo-code-base] load-redemptions/success',
  zefActionSuccessPayload<PromoCodeRedemptionListResponse>()
);

export const redeemPromoCode = createAction(
  '[@zerops/zerops/core/promo-code-base] redeem',
  zefActionPayload<PromoCodeRedeemDto>()
);

export const redeemPromoCodeFail = createAction(
  '[@zerops/zerops/core/promo-code-base] redeem/fail',
  zefActionFailPayload
);

export const redeemPromoCodeSuccess = createAction(
  '[@zerops/zerops/core/promo-code-base] redeem/success',
  zefActionSuccessPayload<PromoCodeRedemption>()
);

// Drops the revealed amount without touching the history list, so the section
// goes back to accepting another code.
export const clearPromoCodeRedemption = createAction(
  '[@zerops/zerops/core/promo-code-base] clear-redemption'
);

// A code handed to a signup call. Held in the store rather than the cookie from
// this point on: the cookie means "still waiting to be used", and once it is in
// a registration body it is not. Survives a failed attempt so a retry that
// succeeds still reveals what the code was worth.
export const promoCodeSubmittedWithSignup = createAction(
  '[@zerops/zerops/core/promo-code-base] submitted-with-signup',
  props<PendingPromoCode>()
);

/**
 * A deep-linked code reached an already-authenticated session. Routing it lives
 * in PromoCodeBaseEffect alongside the post-login case, so both decide with the
 * same rules instead of drifting apart.
 */
export const promoCodeArrived = createAction(
  '[@zerops/zerops/core/promo-code-base] arrived',
  props<{ code: string; }>()
);

/**
 * What the flow endpoint said about a code in hand. Kept so the promo section
 * can offer the top-up up front instead of a Redeem the backend is certain to
 * reject — the routing already asked, and asking twice would be wasteful.
 */
export const promoCodeFlowResolved = createAction(
  '[@zerops/zerops/core/promo-code-base] flow-resolved',
  props<{ code: string; appliesToTopUp: boolean; }>()
);

export const clearSubmittedPromoCode = createAction(
  '[@zerops/zerops/core/promo-code-base] clear-submitted'
);

const all = union({
  loadPromoCodeRedemptions,
  loadPromoCodeRedemptionsFail,
  loadPromoCodeRedemptionsSuccess,
  redeemPromoCode,
  redeemPromoCodeFail,
  redeemPromoCodeSuccess,
  clearPromoCodeRedemption,
  promoCodeSubmittedWithSignup,
  clearSubmittedPromoCode,
  promoCodeArrived,
  promoCodeFlowResolved
});

export type PromoCodeBaseActionUnion = typeof all;
